import { create } from 'zustand';
import { isGloballyHandled } from '../services/api';
import * as circlesService from '../services/circles.service';
import { getEcho, isWsEnabled } from '../services/echo';
import type { NextCircleInfo } from '../types';
import { extractApiError } from '../utils/extractApiError';
import { fireAndForget } from '../utils/fireAndForget';
import { withRetry } from '../utils/retry';
import { useAuthStore } from './auth.store';
import { useGuestUpgradeStore } from './guest-upgrade.store';
import { registerUserScopedReset } from './session-read';

/**
 * Transient = worth retrying: network/timeout (no response) or 5xx/429. A 4xx
 * (401 expired token, 403 gated, 422) fails identically on every attempt —
 * retrying it just triples auth-failure traffic and delays the real handling.
 */
function isTransientNextCircleError(err: unknown): boolean {
  const response = (err as { response?: { status?: number } } | null)?.response;
  if (!response) return true;
  const status = response.status ?? 0;
  return status >= 500 || status === 429;
}

// Ordering token: a retry chain can outlive the next poll tick; a STALE
// chain's result (success or error) must never overwrite a fresher one
// (same pattern as auth.store's userWriteSeq).
let fetchGen = 0;

// Instance-scoped polling state
let autoJoinIntervalRef: ReturnType<typeof setInterval> | null = null;
let autoJoinGeneration = 0;
let wsChannelName: string | null = null;

function clearAutoJoinInterval() {
  autoJoinGeneration++;
  if (autoJoinIntervalRef) {
    clearInterval(autoJoinIntervalRef);
    autoJoinIntervalRef = null;
  }
}

function leaveWsChannel() {
  if (wsChannelName) {
    try {
      getEcho().leave(wsChannelName);
    } catch {
      /* echo may not be initialized */
    }
    wsChannelName = null;
  }
}

interface CircleDiscoveryState {
  nextCircle: NextCircleInfo | null;
  isLoading: boolean;
  isSigningUp: boolean;
  autoJoinTriggeredAt: number | null;
  soloFallbackTriggered: boolean;
  /** Set when user dismisses the solo fallback — prevents re-trigger from polling */
  soloFallbackDismissed: boolean;
  /** The circle ID the user signed up for — used to detect cancellation */
  signedUpCircleId: number | null;
  /** Milliseconds to add to client Date.now() to approximate server time.
   *  Computed from server_time on each /circles/next response. Used by
   *  OnDemandCard's countdown so a fast/slow client clock can't fire the
   *  countdownDone trigger before the server has actually started the circle.
   *  Mirrors the same mechanism in circle-session.store.ts (which derives
   *  it from state.server_time during in-session polls). */
  serverTimeOffsetMs: number;
  error: string | null;
}

interface CircleDiscoveryActions {
  fetchNextCircle: () => Promise<void>;
  signUp: () => Promise<void>;
  cancelSignUp: () => Promise<void>;
  startAutoJoinPolling: () => void;
  stopAutoJoinPolling: () => void;
  /**
   * Hard-clear the auto-join state — stops polling AND drops the
   * autoJoinTriggeredAt flag. Used when the user has navigated back to
   * /app/empathy after leaving a circle: stopAutoJoinPolling alone left
   * the flag set, which kept the "Joining circle..." spinner visible
   * indefinitely (the spinner UI keys off the flag, not the interval).
   */
  clearAutoJoin: () => void;
  dismissSoloFallback: () => void;
  reset: () => void;
}

type CircleDiscoveryStore = CircleDiscoveryState & CircleDiscoveryActions;

export const useCircleDiscoveryStore = create<CircleDiscoveryStore>((set, get) => ({
  nextCircle: null,
  isLoading: false,
  isSigningUp: false,
  autoJoinTriggeredAt: null,
  soloFallbackTriggered: false,
  soloFallbackDismissed: false,
  signedUpCircleId: null,
  serverTimeOffsetMs: 0,
  error: null,

  fetchNextCircle: async () => {
    // Guests: /circles/next is authed — show the clean empty state ("Be the first to
    // sign up!") instead of surfacing an Unauthenticated error in the card.
    if (useAuthStore.getState().isGuest) {
      fetchGen++; // any in-flight authed chain must not repaint the guest card
      set({ nextCircle: null, isLoading: false, error: null });
      return;
    }

    set({ isLoading: true, error: null });
    const gen = ++fetchGen;
    try {
      // Transient-failure tolerance (beta 2026-07-19: a top-of-hour blip painted
      // "Failed to fetch next circle" on the discovery card). Shared withRetry:
      // exponential backoff WITH jitter (a fixed schedule would re-synchronize
      // the exact :00 thundering herd this guards against), 4xx never retried.
      // Retries only when there is NO card yet — background refreshes fail soft
      // (last-known data stays up and the next poll is seconds away), so a
      // slow-backend tick can't wedge the auto-join poll for ~45s.
      const attempts = get().nextCircle === null ? 3 : 1;
      const data = await withRetry(() => circlesService.getNextCircle(), {
        maxAttempts: attempts,
        baseDelay: 800,
        maxDelay: 4000,
        isRetryable: isTransientNextCircleError,
      });
      if (gen !== fetchGen) return; // superseded by a newer fetch — drop
      const updates: Partial<CircleDiscoveryState> = { nextCircle: data, isLoading: false };
      // Recompute serverTimeOffsetMs whenever the response carries server_time.
      // Clients with a fast clock will get a negative offset; slow clocks get positive.
      // OnDemandCard reads this to keep its countdown anchored to server time.
      if (data.server_time) {
        const serverMs = new Date(data.server_time).getTime();
        if (Number.isFinite(serverMs)) {
          updates.serverTimeOffsetMs = serverMs - Date.now();
        }
      }
      // Backend flag: unclaimed solo fallback available (don't re-trigger if user dismissed)
      if (
        data.solo_fallback_available &&
        !get().soloFallbackTriggered &&
        !get().soloFallbackDismissed
      ) {
        updates.soloFallbackTriggered = true;
        updates.signedUpCircleId = null;
      }
      set(updates);
    } catch (error) {
      if (gen !== fetchGen) return; // a fresher chain already painted the card
      const msg = extractApiError(error, 'Failed to fetch next circle');
      set({ error: msg, isLoading: false });
    }
  },

  signUp: async () => {
    // Guest: open the standard upgrade prompt (same visual language as GuestWall)
    // instead of firing an authed call — in-page actions gate via the modal.
    if (useAuthStore.getState().isGuest) {
      useGuestUpgradeStore.getState().open({
        title: 'Circles need an account',
        description:
          'Circles are live audio sessions with real people. Create your free account to sign up for the next one.',
      });
      return;
    }

    if (get().isSigningUp) return;
    set({ isSigningUp: true, error: null });
    try {
      await circlesService.signUpForCircle();
      await get().fetchNextCircle();
      // Track which circle we signed up for
      const circle = get().nextCircle;
      set({
        isSigningUp: false,
        signedUpCircleId: circle?.circle_id ?? null,
        soloFallbackDismissed: false, // Fresh signup = fresh opportunity
      });
    } catch (error) {
      // A 402 (signUp() now refuses an unaffordable signup, C13) was answered by
      // the global out-of-credits prompt: one answer to one refusal, no error here.
      if (isGloballyHandled(error)) {
        set({ isSigningUp: false });
        throw error;
      }
      const msg = extractApiError(error, 'Failed to sign up');
      set({ error: msg, isSigningUp: false });
      throw error;
    }
  },

  cancelSignUp: async () => {
    if (get().isSigningUp) return;
    set({ isSigningUp: true, error: null });
    try {
      await circlesService.cancelCircleSignup();
      await get().fetchNextCircle();
      set({ isSigningUp: false, signedUpCircleId: null });
    } catch (error) {
      const msg = extractApiError(error, 'Failed to cancel signup');
      set({ error: msg, isSigningUp: false });
      throw error;
    }
  },

  startAutoJoinPolling: () => {
    clearAutoJoinInterval();
    leaveWsChannel();

    const currentGen = ++autoJoinGeneration;

    // Handler for both WS events and poll results.
    //
    // 2026-04-10 cleanup: removed the "tracked circle ID changed → trigger
    // soloFallback" heuristic. It was a workaround for a backend bug that's
    // since been fixed (CircleSchedulingService::getNextCircleInfo now
    // suppresses solo_fallback_available correctly when the user has
    // completed a real circle since the cancellation). The heuristic was
    // ALSO firing false-positives after a normal successful session: the
    // user finishes a circle, comes back to /app/empathy, the backend
    // returns next-hour's circle which has a different ID, and the
    // heuristic mistook that for a cancellation. The fetchNextCircle path
    // (lines 64-79 above) already sets soloFallbackTriggered correctly
    // based on the backend's solo_fallback_available flag, so the heuristic
    // was redundant AND wrong.
    const checkCircleState = () => {
      if (currentGen !== autoJoinGeneration) return;

      const { nextCircle: circle } = get();
      if (!circle) return;

      if (circle.status === 'in_progress') {
        get().stopAutoJoinPolling();
        set({ autoJoinTriggeredAt: Date.now() });
      }
      // Note: cancelled circles are no longer special-cased here. If a
      // cancellation results in an unclaimed solo fallback, the backend
      // sets solo_fallback_available=true on the NEXT fetchNextCircle and
      // the store updates the flag accordingly via fetchNextCircle's own
      // logic. Polling continues until the circle goes in_progress or the
      // user dismisses.
    };

    // Subscribe to WS channel for instant notifications
    const circleId = get().signedUpCircleId;
    if (isWsEnabled() && circleId) {
      try {
        const channelName = `circle.signup.${circleId}`;
        wsChannelName = channelName;
        getEcho()
          .private(channelName)
          .listen('.circle.discovery.changed', async () => {
            if (currentGen !== autoJoinGeneration) return;
            await get().fetchNextCircle();
            checkCircleState();
          });
      } catch (e) {
        console.warn('Circle discovery WS subscription failed, using polling only', e);
      }
    }

    // Fallback polling: slow (15s) with WS, fast (3s) without
    const pollInterval = isWsEnabled() && circleId ? 15000 : 3000;
    let isPollingFetch = false;
    autoJoinIntervalRef = setInterval(
      fireAndForget(async () => {
        if (isPollingFetch || currentGen !== autoJoinGeneration) return;
        isPollingFetch = true;
        try {
          await get().fetchNextCircle();
          checkCircleState();
        } finally {
          isPollingFetch = false;
        }
      }),
      pollInterval,
    );
  },

  stopAutoJoinPolling: () => {
    clearAutoJoinInterval();
    leaveWsChannel();
  },

  clearAutoJoin: () => {
    clearAutoJoinInterval();
    leaveWsChannel();
    set({ autoJoinTriggeredAt: null });
  },

  dismissSoloFallback: () => {
    set({ soloFallbackTriggered: false, soloFallbackDismissed: true, signedUpCircleId: null });
  },

  reset: () => {
    // Identity clear (logout / guest entry, Codex #709): a retry chain still
    // in flight for the PREVIOUS user must never resolve into the cleared
    // store — bump the generation so its writes are dropped.
    fetchGen++;
    get().stopAutoJoinPolling();
    set({
      nextCircle: null,
      isLoading: false,
      isSigningUp: false,
      autoJoinTriggeredAt: null,
      soloFallbackTriggered: false,
      soloFallbackDismissed: false,
      signedUpCircleId: null,
      serverTimeOffsetMs: 0,
      error: null,
    });
  },
}));

// Per-user: discovery flags (auto-join / solo-fallback) are identity-scoped;
// reset synchronously on every session change and on entering the guest tour (#940 r9).
registerUserScopedReset(() => useCircleDiscoveryStore.getState().reset());
